/**
 * The user roster, as the admin screen sees it (P0-BR1, D-016).
 *
 * Thin on purpose. Every rule about who may do what lives in
 * `server/src/modules/users/users.service.ts` — an admin cannot change their
 * own role, cannot deactivate themselves, and the `system` account is not a
 * person. This file does not restate any of that; it sends the request and
 * lets the server answer (AP-1).
 *
 * The one thing worth noticing here is what is *absent*: no password ever comes
 * back. `PublicUser` on the server strips the hash, so there is nothing in this
 * cache that would matter if it leaked into a devtools panel.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { api } from '../../api/client.js';
import type { AuthUser } from '../auth/AuthContext.js';

/** Exactly the shape `GET /users` returns — the same one `/auth/me` gives. */
export type User = AuthUser;

export type AssignableRole = 'admin' | 'partner' | 'staff';

interface Envelope<T> {
  success: true;
  data: T;
}

export const userKeys = {
  all: () => ['users'] as const,
};

/**
 * Every signed-in partner may read the roster — §1.4 makes read transparency
 * absolute, and the server enforces the same. Only the controls are admin-only.
 */
export function useUsers(): UseQueryResult<User[]> {
  return useQuery({
    queryKey: userKeys.all(),
    queryFn: async () => (await api.get<Envelope<User[]>>('/users')).data.data,
  });
}

export interface NewUser {
  fullName: string;
  phone: string;
  password: string;
  email?: string;
  role: AssignableRole;
  language: 'bn' | 'en';
}

export interface UserChanges {
  fullName?: string;
  email?: string | null;
  language?: 'bn' | 'en';
  role?: AssignableRole;
  isActive?: boolean;
  password?: string;
}

/**
 * Adding or changing a partner moves numbers on other screens.
 *
 * The Partners screen lists everyone with role `admin` or `partner` and divides
 * ownership across them (BR-01), so a role change or a new account alters what
 * that screen shows. Invalidating it here means the two cannot disagree.
 */
function useRosterInvalidation(): () => Promise<void> {
  const queryClient = useQueryClient();

  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: userKeys.all() }),
      queryClient.invalidateQueries({ queryKey: ['partners'] }),
    ]);
  };
}

export function useCreateUser(): UseMutationResult<User, unknown, NewUser> {
  const invalidate = useRosterInvalidation();

  return useMutation({
    mutationFn: async (input: NewUser) => (await api.post<Envelope<User>>('/users', input)).data.data,
    onSuccess: invalidate,
  });
}

export function useUpdateUser(): UseMutationResult<User, unknown, { id: number; changes: UserChanges }> {
  const invalidate = useRosterInvalidation();

  return useMutation({
    mutationFn: async ({ id, changes }) => (await api.patch<Envelope<User>>(`/users/${String(id)}`, changes)).data.data,
    onSuccess: invalidate,
  });
}
